import React, { useState, useEffect, useRef } from 'react';
import logo from '../../Assets/Hero/logonavbar.png';
import { useLanguage } from '../../App';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { language, changeLanguage, t } = useLanguage();
  const dropdownRef = useRef(null);

  const isRTL = language === 'ar';

  const buttonGradientStyle = {
    background: 'radial-gradient(50% 50% at 50% 50%, #1A4D90 0%, #0C2341 100%)'
  };

  useEffect(() => {
    const handleScrollEvent = () => {
      setScrolled(window.scrollY > 20);

      const sections = ['home', 'about', 'services', 'features', 'why-us', 'contact'];
      const scrollPosition = window.scrollY + 150;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const height = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
            setActiveSection(section);
          }
        }
      }
    };

    window.addEventListener('scroll', handleScrollEvent);
    return () => window.removeEventListener('scroll', handleScrollEvent);
  }, []);

  const handleScroll = (e, targetId) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (!target) return;

    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset;
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    let startTime = null;
    const duration = 800;

    function animation(currentTime) {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const run = (t, b, c, d) => {
        t /= d;
        return -c * t * (t - 2) + b;
      };
      window.scrollTo(0, run(timeElapsed, startPosition, distance, duration));
      if (timeElapsed < duration) requestAnimationFrame(animation);
    }
    requestAnimationFrame(animation);
    setIsMenuOpen(false); 
  };

  const languages = [
    { code: 'ar', label: 'AR', name: 'العربية' },
    { code: 'en', label: 'EN', name: 'English' },
  ];
  const currentLang = languages.find(l => l.code === language) || languages[0];

  const navItems = [
    { id: 'home', label: 'home' },
    { id: 'about', label: 'about' },
    { id: 'services', label: 'services' },
    { id: 'features', label: 'features' },
    { id: 'why-us', label: 'whyUs' },
  ];

  return (
    <nav 
      dir={isRTL ? 'rtl' : 'ltr'} 
      className={`fixed top-0 left-0 w-full z-[1000] transition-all duration-300 font-alexandria ${
        scrolled ? 'bg-[#030B15]/95 backdrop-blur-md py-1 shadow-2xl border-b border-white/5' : 'bg-transparent py-3'
      }`}
    >
      <div className="max-w-[1440px] mx-auto min-h-[60px] lg:min-h-[70px] flex items-center justify-between px-6 md:px-12 lg:px-[104px]">
        
        <div className="flex-shrink-0 order-1">
          <img src={logo} alt="Logo" className="w-[100px] lg:w-[125px] h-auto object-contain" />
        </div>

        <div className="hidden xl:flex items-center justify-center gap-x-6 order-2 flex-grow">
          {navItems.map((item) => (
            <a 
              key={item.id} 
              href={`#${item.id}`} 
              onClick={(e) => handleScroll(e, item.id)}
              className={`text-[15px] 2xl:text-[17px] transition-all duration-300 relative py-1 ${
                activeSection === item.id ? 'text-[#1A4D90] font-bold' : 'text-white font-light hover:text-[#1A4D90]'
              }`}
            >
              {t(`navbar.${item.label}`)}
              <span className={`absolute bottom-0 left-0 h-[2px] bg-[#1A4D90] transition-all duration-300 ${activeSection === item.id ? 'w-full' : 'w-0'}`}></span>
            </a>
          ))}
        </div>

        <div className={`hidden xl:flex items-center gap-4 order-3 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
          <a 
            href="#contact" 
            onClick={(e) => handleScroll(e, 'contact')} 
            style={buttonGradientStyle}
            className="min-w-[130px] h-[40px] rounded-[8px] text-white flex items-center justify-center hover:brightness-110 transition-all text-xs font-medium"
          >
            {t('buttons.contactUs')}
          </a>

          <div className="relative" ref={dropdownRef}>
            <button 
              onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)} 
              style={buttonGradientStyle}
              className="px-3 h-[40px] rounded-[8px] border border-[#1A4D90]/30 text-white flex items-center gap-2 text-xs hover:brightness-110 transition-all"
            >
              {currentLang.label}
              <svg className={`w-4 h-4 transition-transform ${isLanguageMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
            </button>
            {isLanguageMenuOpen && (
              <div className={`absolute top-[110%] mt-2 w-28 bg-[#030B15] border border-[#1A4D90] rounded-lg overflow-hidden shadow-2xl ${isRTL ? 'left-0' : 'right-0'}`}>
                {languages.map((lang) => (
                  <button key={lang.code} onClick={() => { changeLanguage(lang.code); setIsLanguageMenuOpen(false); }} className="w-full px-4 py-2 text-white hover:bg-[#1A4D90] text-center text-xs transition-colors">
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="xl:hidden order-3">
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white text-3xl">
            {isMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <>
          <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)}></div>
          <div className={`fixed top-0 h-screen w-72 bg-[#030B15] z-[130] transition-transform duration-300 ${isRTL ? 'left-0' : 'right-0'}`}>
             <div className="p-8 flex flex-col h-full">
                <div className="flex flex-col items-center mb-10 relative"> 
                   <button onClick={() => setIsMenuOpen(false)} className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} text-white text-2xl`}>✕</button>
                   <img src={logo} alt="Logo" className="w-32 h-auto object-contain mt-4" />
                </div>

                <div className="flex flex-col gap-6 mb-8 text-center"> 
                   {navItems.map(item => (
                     <a 
                       key={item.id} 
                       href={`#${item.id}`} 
                       onClick={(e) => handleScroll(e, item.id)}
                       className={`text-lg transition-colors ${activeSection === item.id ? 'text-[#1A4D90] font-bold' : 'text-white/80 font-light'}`}
                     >
                       {t(`navbar.${item.label}`)}
                     </a>
                   ))}
                   <a 
                    href="#contact" 
                    onClick={(e) => handleScroll(e, 'contact')} 
                    className="text-lg font-bold mt-2 py-2 rounded-lg"
                    style={buttonGradientStyle}
                   >
                     {t('buttons.contactUs')}
                   </a>
                </div>

                <div className="pt-6 border-t border-white/10"> 
                   <p className="text-white/40 text-[10px] mb-3 uppercase tracking-widest text-center">{isRTL ? 'تغيير اللغة' : 'Change Language'}</p>
                   <div className="grid grid-cols-2 gap-2" dir="rtl">
                     {languages.map((lang) => (
                       <button 
                         key={lang.code} 
                         onClick={() => { changeLanguage(lang.code); setIsMenuOpen(false); }}
                         className={`py-2 rounded-lg border transition-all duration-300 text-xs font-medium ${
                           language === lang.code ? 'text-white shadow-lg' : 'border-white/10 text-white/60'
                         }`}
                         style={language === lang.code ? buttonGradientStyle : {}}
                       >
                         {lang.name}
                       </button>
                     ))}
                   </div>
                </div>
             </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navbar;